// Bell in the top bar: unread badge + dropdown with all notifications.
import { useT } from '@/i18n';
import { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { Segmented } from '@/components/ui/Controls';
import { Icon } from '@/components/ui/Icon';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { useTime } from '@/hooks/useTime';
import { cx } from '@/lib/format';
import type { Notice } from '@/types';
import { KIND_LABEL, NoticeIcon } from './NoticeIcon';
import { useNotices } from './NoticesProvider';


export function BellMenu() {
  const { notices, unread, open, markAll, toggleRead } = useNotices();
  const [show, setShow] = useState(false);
  const [tab, setTab] = useState<'all' | 'urgent'>('all');
  const root = useRef<HTMLDivElement>(null);
  const id = useId();
  const { pathname } = useLocation();
  useEffect(() => setShow(false), [pathname]);
  useEffect(() => {
    if (!show) return;
    const away = (e: PointerEvent) => { if (!root.current?.contains(e.target as Node)) setShow(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setShow(false); };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('pointerdown', away); document.removeEventListener('keydown', esc); };
  }, [show]);

  // API order: newest first
  const { channels } = useAlertsSafe();
  const { t } = useT('notices');
  const browserOff = !channels.site ? t('siteOff') : '';
  const list = tab === 'urgent' ? notices.filter((n) => n.urgent) : notices;
  const tf = useTime();
  const days = tf.byDay(list);
  const item = (n: Notice) => (
    <li key={n.id} className="bn-li">
      <button type="button" className="bn-dot" aria-label={n.read ? t('markUnread') : t('markRead')} title={n.read ? t('markUnread') : t('markRead')} onClick={() => toggleRead(n.id)}><i className={cx(!n.read && 'on')} /></button>
      <button type="button" className={cx('bn-i', !n.read && 'new', n.urgent && 'urgent')} onClick={() => { setShow(false); open(n); }}>
        <NoticeIcon n={n} />
        <span className="bn-t">
          <span className="bn-k">{KIND_LABEL[n.kind]}{n.urgent && <em>{t('urgentTag')}</em>}</span>
          <b>{n.title}</b>
          <small>{n.body}</small>
        </span>
        <time dateTime={n.at}>{tf.time(n.at)}</time>
      </button>
    </li>
  );

  return (
    <div className="bell" ref={root}>
      <button className={cx('cbtn sm', show && 'on')} type="button" aria-label={unread ? t('bellUnread', { count: unread }) : t('title')} aria-expanded={show} aria-controls={id} onClick={() => setShow(!show)}>
        <svg viewBox="0 0 24 24"><path d="M6 16V11a6 6 0 1 1 12 0v5l1.5 2h-15z" /><path d="M10 20.5a2 2 0 0 0 4 0" /></svg>
        {unread > 0 && <b className="bell-n">{unread > 9 ? '9+' : unread}</b>}
      </button>
      {show && (
        <div className="bn-panel" id={id} role="dialog" aria-label={t('title')}>
          <header className="bn-h">
            <div><b>{t('title')}</b><small>{unread ? t('unread', { count: unread }) : t('allRead')}</small></div>
            {unread > 0 && <button type="button" className="bn-all" onClick={markAll}>{t('readAll')}</button>}
          </header>
          <Segmented tabs className="sr-seg bn-tabs" label={t('filter')} value={tab} onChange={setTab}
            options={[{ value: 'all', label: t('tabAll', { n: notices.length }) }, { value: 'urgent', label: t('tabUrgent', { n: notices.filter((n) => n.urgent).length }) }]} />
          <div className="bn-list">
            {!list.length && (
              <div className="bn-empty">
                <span className="stc-mark sm" aria-hidden="true"><i /><i /><b><Icon name="bell" /></b></span>
                <b>{tab === 'urgent' ? t('emptyUrgent') : t('empty')}</b>
                <p>{t('emptyText')}</p>
              </div>
            )}
            {days.map((d) => (
              <section key={d.key}>
                <h5>{d.label}</h5>
                <ul>{d.items.map(item)}</ul>
              </section>
            ))}
          </div>
          {browserOff && <Link className="bn-warn" to="/alerts#channels" onClick={() => setShow(false)}><Icon name="alert" /><span>{browserOff}</span><em>{t('common:action.settings')}</em></Link>}
          <footer className="bn-f">
            <Link to="/alerts#channels" onClick={() => setShow(false)}>{t('setup')}</Link>
            <span>{t('telegramToo')}</span>
          </footer>
        </div>
      )}
    </div>
  );
}

/** The bell sits in every top bar; alerts may still be loading or failed there — never suspend it. */
function useAlertsSafe() {
  const { data } = useQuery({ queryKey: ['alerts'], queryFn: api.alerts });
  return { channels: data?.channels ?? { site: true } };
}
